/**
 * Preços/oferta das 6 Certificações FEO (fonte de verdade única).
 *
 * Dois níveis, iguais às LPs atuais:
 * - PADRÃO: sete certificações de R$697 por R$397 (12x R$41,06 com juros) · cupom `PROMO`
 *
 * Order bump em todas: as outras certificações com R$600 OFF direto no carrinho.
 */
import { CERT_SLUGS, type CertSlug } from "./config";

/** IDs de produto na Eduzz (turma atual). */
export const EDUZZ_IDS: Record<CertSlug, string> = {
  lombar: "G92VQY5Z9E",
  cervical: "2439321",
  eletro: "VWGZ1J2P07",
  ombro: "D0RAA28B9Y",
  tornozelo: "69KAZ55A0O",
  joelho: "1W32DY3592",
};

export const BUMP =
  "Ao se matricular você garante R$600,00 de desconto em todas as outras Certificações da FEO.";

export type CertOferta = {
  /** true = está no lote promocional mais baixo (R$400 OFF, sem juros). */
  promo: boolean;
  cupom: string;
  /** Ex.: "VALOR PROMOCIONAL · R$400 OFF" */
  descontoLabel: string;
  /** Ex.: "R$400 OFF" */
  descontoCurto: string;
  precoDe: string;
  precoDeValor: string;
  /** Destaque de preço do card/LP. */
  parcelado: string;
  /** Complemento do preço. */
  aVista: string;
  aVistaValor: string;
  /** Nota curta sobre o parcelamento. */
  parcelasNota: string;
  bump: string;
};

const PADRAO: Omit<CertOferta, "cupom" | "promo"> = {
  descontoLabel: "DESCONTO ESPECIAL · R$300 OFF",
  descontoCurto: "R$300 OFF",
  precoDe: "DE R$697 POR",
  precoDeValor: "R$697",
  parcelado: "R$397,00",
  aVista: "ou 12x R$41,06",
  aVistaValor: "R$397",
  parcelasNota: "12x R$41,06",
  bump: BUMP,
};

/**
 * Oferta da ação Semana da Fisioterapia (72% OFF): todas as certificações
 * de R$697 por R$197 (ou 12x com juros) · cupom `SEMANADAFISIO`.
 */
const ACAO_SEMANA: Omit<CertOferta, "cupom" | "promo"> = {
  descontoLabel: "SEMANA DA FISIOTERAPIA · 72% OFF",
  descontoCurto: "72% OFF",
  precoDe: "DE R$697 POR",
  precoDeValor: "R$697",
  parcelado: "12x R$21",
  aVista: "ou R$197,00 à vista",
  aVistaValor: "R$197",
  parcelasNota: "12x R$20,37",
  bump: BUMP,
};

export const CERT_OFERTA_SEMANA: CertOferta = {
  ...ACAO_SEMANA,
  promo: true,
  cupom: "SEMANADAFISIO",
};

/** Oferta vigente considerando a ação Semana da Fisioterapia. */
export function certOfertaAtiva(slug: CertSlug, acaoAtiva: boolean): CertOferta {
  return acaoAtiva ? CERT_OFERTA_SEMANA : CERT_OFERTA[slug];
}

/** URL de checkout considerando a ação Semana da Fisioterapia. */
export function certCheckoutUrlAtiva(slug: CertSlug, acaoAtiva: boolean): string {
  if (!acaoAtiva) return certCheckoutUrl(slug);
  return `https://chk.eduzz.com/${EDUZZ_IDS[slug]}?cupom=${CERT_OFERTA_SEMANA.cupom}&df=1&pf=1`;
}

/** Todas usam a mesma condição habitual, sem limitar parcelas a 10x. */
export const CERT_PROMO: readonly CertSlug[] = [];

export const CERT_OFERTA: Record<CertSlug, CertOferta> = CERT_SLUGS.reduce(
  (acc, slug) => {
    acc[slug] = { ...PADRAO, promo: false, cupom: "PROMO" };
    return acc;
  },
  {} as Record<CertSlug, CertOferta>,
);

export function certOferta(slug: CertSlug): CertOferta {
  return CERT_OFERTA[slug];
}

/**
 * Oferta usada nas 6 LPs de venda do Lovable (noindex, em construção).
 * Mesma fonte do catálogo /certificacao: R$397 com cupom PROMO.
 */
export function certOfertaLp(slug: CertSlug): CertOferta {
  return CERT_OFERTA[slug];
}

export function certCheckoutUrlLp(slug: CertSlug): string {
  return certCheckoutUrl(slug);
}


export function certCheckoutUrl(slug: CertSlug): string {
  const o = CERT_OFERTA[slug];
  return `https://chk.eduzz.com/${EDUZZ_IDS[slug]}?cupom=${o.cupom}&df=1&pf=1`;
}

/** Fim do contador: domingo 23:59:59 da semana corrente (renova a cada semana). */
export function promoDeadline(now: Date = new Date()): Date {
  const end = new Date(now);
  const diff = (7 - end.getDay()) % 7;
  end.setDate(end.getDate() + diff);
  end.setHours(23, 59, 59, 999);
  if (end.getTime() - now.getTime() < 60_000) end.setDate(end.getDate() + 7);
  return end;
}

/**
 * Descrição curta de cada certificação para o catálogo.
 * Mantidas no mesmo tamanho (~150 caracteres) para os cards ficarem alinhados.
 */
export const CERT_RESUMO: Record<CertSlug, string> = {
  lombar:
    "Raciocínio clínico atualizado para a dor lombar: triagem, exame físico, exercício e educação em dor, com condutas validadas pela ciência.",
  cervical:
    "Cervicalgias, cefaleias e tontura cervicogênica sob controle: avaliação segura, terapia manual e progressão de exercícios com evidência.",
  eletro:
    "Domine parâmetros e dosimetria dos agentes eletrofísicos e saiba exatamente quando (e por que) cada recurso ajuda o seu paciente.",
  ombro:
    "Do impacto às lesões do manguito: diagnóstico diferencial, testes que realmente importam e programas de carga para devolver função.",
  tornozelo:
    "Entorses, tendinopatias e dores no pé: avaliação passo a passo, retorno ao esporte e protocolos pós-trauma aplicados na prática.",
  joelho:
    "Patelofemoral, LCA e artrose no mesmo lugar: avaliação estruturada, intervenções ativas e protocolos pós-cirúrgicos bem definidos.",
};

export const CERT_ORDER = CERT_SLUGS;

/** Certificação em Quadril · oferta exclusiva de lançamento (venda direta). */
export const QUADRIL_CHECKOUT_URL = "https://chk.eduzz.com/G96RR16QW1?cupom=PROMO&df=1&pf=1";
export const QUADRIL_OFERTA: CertOferta = {
  ...PADRAO,
  promo: false,
  cupom: "PROMO",
  bump: "",
};

/** Prazo da oferta da Quadril: próxima segunda ou quinta às 23h59 (horário de Brasília, UTC-3). */
export function quadrilDeadline(now: Date = new Date()): Date {
  const BRT = -3 * 3600 * 1000;
  const local = new Date(now.getTime() + BRT);
  for (let add = 0; add < 8; add++) {
    const d = new Date(Date.UTC(local.getUTCFullYear(), local.getUTCMonth(), local.getUTCDate() + add, 23, 59, 59));
    const dow = d.getUTCDay();
    if ((dow === 1 || dow === 4) && d.getTime() > local.getTime()) return new Date(d.getTime() - BRT);
  }
  return new Date(now.getTime() + 86400000);
}
